import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SID_KEY = "lexv_tutor_session";
const sessionId = () => {
  let s = localStorage.getItem(SID_KEY);
  if (!s) { s = `tutor-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem(SID_KEY, s); }
  return s;
};
const SUGGEST = ["How is the VEPT scored?", "Tips for Sentence Builds", "How do I reach 61+?"];

const streamChat = async (message, onDelta) => {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tutor/chat`, {
    method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("lexv_token") || ""}` }, body: JSON.stringify({ session_id: sessionId(), message }),
  });
  if (!res.ok) throw new Error(res.status === 429 ? "Too many questions — try again in a few minutes." : res.status === 403 ? "The AI Tutor is included with a Practice Pass." : res.status === 401 ? "Please sign in again to chat with the tutor." : "The tutor is unavailable right now.");
  const reader = res.body.getReader(), dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split("\n\n"); buf = lines.pop();
    for (const l of lines) {
      const d = l.replace(/^data: /, "");
      if (d === "[DONE]") return;
      const j = JSON.parse(d);
      if (j.error) throw new Error("The tutor is unavailable right now.");
      onDelta(j.delta);
    }
  }
};

export const TutorChat = () => {
  const { hasAccess, user } = useAuth();
  return hasAccess ? <TutorChatInner key={user?.id} /> : null;
};

const TutorChatInner = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { if (open && !msgs.length) api.get("/tutor/history", { params: { session_id: sessionId() } }).then(({ data }) => setMsgs((m) => (m.length ? m : data))).catch(() => {}); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs]);
  if (pathname.startsWith("/exam") || ["/login", "/pricing", "/admin", "/contact", "/payment"].some((p) => pathname.startsWith(p))) return null;

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    const upd = (fn) => setMsgs((m) => { if (!m.length) return m; const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: fn(c[c.length - 1].content) }; return c; });
    try { await streamChat(q, (d) => upd((c) => c + d)); }
    catch (e) { upd(() => e.message); }
    finally { setBusy(false); }
  };

  return (
    <>
      {!open && <button data-testid="tutor-open-btn" onClick={() => setOpen(true)} className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-3 text-sm font-bold text-white shadow-2xl shadow-indigo-900/50 transition-transform hover:-translate-y-0.5"><MessageCircle className="h-5 w-5" />AI Tutor</button>}
      {open && (
        <div data-testid="tutor-panel" className="fixed bottom-4 right-4 z-50 flex h-[min(560px,80vh)] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="flex items-center justify-between bg-slate-900 px-4 py-3 text-white">
            <div><div className="font-display font-bold">AI Tutor</div><div className="font-mono text-[10px] text-slate-400">GPT 6 Luna · VEPT coach</div></div>
            <button data-testid="tutor-close-btn" onClick={() => setOpen(false)} className="rounded-full p-1 hover:bg-slate-800" aria-label="Close"><X className="h-5 w-5" /></button>
          </div>
          <div data-testid="tutor-messages" className="flex-1 space-y-3 overflow-y-auto p-4 text-sm">
            {!msgs.length && (
              <div className="space-y-2 text-slate-500"><p>Ask anything about grammar, vocabulary or VEPT strategy.</p>
                {SUGGEST.map((s) => <button key={s} data-testid={`tutor-suggest-${s.slice(0, 10).replace(/\W/g, "")}`} onClick={() => send(s)} className="block rounded-full border border-slate-200 px-3 py-1 text-left text-xs text-slate-700 hover:border-indigo-400">{s}</button>)}</div>
            )}
            {msgs.map((m, i) => (
              <div key={i} data-testid={`tutor-msg-${m.role}`} className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 ${m.role === "user" ? "ml-auto bg-indigo-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                {m.content.replace(/\*\*/g, "") || <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t border-slate-200 p-3">
            <input data-testid="tutor-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask the tutor…" className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            <button data-testid="tutor-send-btn" disabled={busy || !input.trim()} className="rounded-full bg-indigo-600 p-2.5 text-white disabled:opacity-40" aria-label="Send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
          </form>
        </div>
      )}
    </>
  );
};
